import React, {
  createContext, useMemo, useState, useCallback, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import { sendRequest, shootAPI } from '../../api/shootAPI';
import API from '../../api/endpoints';

const savedUserToken = localStorage.getItem('shoot_user_token') ?? null;
const savedToken = localStorage.getItem('shoot_auth_token') ?? null;
const savedRefreshToken = localStorage.getItem('shoot_refresh_token') ?? null;
if (savedToken) shootAPI.defaults.headers.common.Authorization = `Bearer ${savedToken}`;
else {
  // If a full auth token wasn't found, check for an incomplete sign in (2FA token)
  const savedTwofactorToken = localStorage.getItem('shoot_twofactor_token') ?? null;
  if (savedToken) shootAPI.defaults.headers.common.Authorization = `Bearer ${savedTwofactorToken}`;
  else if (savedUserToken) shootAPI.defaults.headers.common.Authorization = `Bearer ${savedUserToken}`;
}

const savedUser = localStorage.getItem('shoot_user') ? JSON.parse(localStorage.getItem('shoot_user')) : null;
const savedMember = localStorage.getItem('shoot_member') ? JSON.parse(localStorage.getItem('shoot_member')) : null;
const savedWorkspaces = localStorage.getItem('shoot_workspaces')
  ? JSON.parse(localStorage.getItem('shoot_workspaces')) : null;
const savedActiveWorkspace = localStorage.getItem('shoot_active_workspace')
  ? JSON.parse(localStorage.getItem('shoot_active_workspace')) : null;
const savedImpersonatedBy = localStorage.getItem('shoot_impersonated_by')
  ? JSON.parse(localStorage.getItem('shoot_impersonated_by')) : null;

const DEFAULT_DATA = {
  user: savedUser, // data of currently logged in user
  member: savedMember,
  impersonatedBy: savedImpersonatedBy,
  twoFactorToken: null, // auth token for generating and validating 2FA codes through the Shoot API
  workspaces: savedWorkspaces, // list of available workspaces associated with user
  activeWorkspace: savedActiveWorkspace, // the active workspace
  refreshSessionTimeout: null,
  secretKey: null,
  qrCode: null,
};

const DEFAULT_TOKENS = {
  refreshToken: savedRefreshToken,
  accessToken: savedToken,
  userToken: savedUserToken,
};

export const UserContext = createContext();

export function UserProvider({ children }) {
  const history = useHistory();
  const [data, setData] = useState(DEFAULT_DATA);
  const [tokens, setTokens] = useState(DEFAULT_TOKENS);

  const redirect = (route) => history.push(route);

  const setWorkspaceMember = (accessToken) => {
    const {
      user, member, workspace, impersonatedBy = null,
    } = jwtDecode(accessToken);
    setData({
      ...data, user, member, activeWorkspace: workspace, impersonatedBy,
    });
    localStorage.setItem('shoot_user', JSON.stringify(user));
    localStorage.setItem('shoot_member', JSON.stringify(member));
    localStorage.setItem('shoot_active_workspace', JSON.stringify(workspace));
    if (impersonatedBy) {
      localStorage.setItem('shoot_impersonated_by', JSON.stringify(impersonatedBy));
    } else {
      localStorage.removeItem('shoot_impersonated_by');
    }

    // Store permissions separately for quick access
    if (member?.role?.permissions) {
      const permissions = member.role.permissions.map((p) => p.name);
      localStorage.setItem('shoot_permissions', JSON.stringify(permissions));
    } else {
      // Clear permissions if not present
      localStorage.removeItem('shoot_permissions');
    }
  };

  const updateTokens = useCallback(({
    refreshToken, accessToken, userToken, twoFactorToken, secretKey,
  }) => {
    // add token to all future requests to Shoot API
    if (twoFactorToken) shootAPI.defaults.headers.common.Authorization = `Bearer ${twoFactorToken}`;
    else if (accessToken) shootAPI.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    else if (userToken) shootAPI.defaults.headers.common.Authorization = `Bearer ${userToken}`;
    if (accessToken) {
      localStorage.setItem('shoot_auth_token', accessToken);
      setWorkspaceMember(accessToken);
    }
    if (refreshToken) localStorage.setItem('shoot_refresh_token', refreshToken);
    if (userToken) localStorage.setItem('shoot_user_token', userToken);
    setTokens((prev) => ({
      accessToken: accessToken ?? prev.accessToken,
      refreshToken: refreshToken ?? prev.refreshToken,
      userToken: userToken ?? prev.userToken,
      twoFactorToken: twoFactorToken ?? prev.twoFactorToken,
      secretKey: secretKey ?? prev.secretKey,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = ({ userToken, refreshToken }) => {
    if (userToken) {
      updateTokens({ userToken, refreshToken });
      const { user } = jwtDecode(userToken);
      setData({
        ...data, user,
      });
      localStorage.setItem('shoot_user', JSON.stringify(user));
    }
  };

  const selectWorkspace = async (workspaceId, userInfo) => {
    let query = { workspaceId };
    if (!data.impersonatedBy && data.user.role === 'Superadmin' && userInfo) {
      query = { ...query, userInfo };
    }
    try {
      const res = await sendRequest({
        method: 'post',
        url: API.AUTH.workspace,
        data: query,
        ...(data.impersonatedBy ? {
          headers: {
            'X-Refresh-Token': localStorage.getItem('shoot_refresh_token'),
          },
        } : {}),
      });
      if (res.success) {
        const {
          accessToken, refreshToken, twoFactorToken, qrCode, secretKey,
        } = res.data;
        updateTokens({
          accessToken, refreshToken, twoFactorToken, secretKey,
        });
        if (qrCode) {
          return {
            success: true, qrCode, twoFactorToken, accessToken: null, secretKey,
          };
        }
        if (twoFactorToken) { // Two-factor method of this workspace is active
          return { success: true, twoFactorToken, accessToken: null };
        }

        setWorkspaceMember(accessToken);
        return { success: true, twoFactorToken: null, accessToken };
      }
      return { success: false };
    } catch {
      // eslint-disable-next-line no-console
      console.log('Sign in workspace failed.');
      return { success: false };
    }
  };

  const startImpersonation = useCallback(async (memberId) => {
    const response = await sendRequest({
      method: 'post',
      url: API.AUTH.impersonate,
      data: { memberId },
      headers: {
        'X-Refresh-Token': localStorage.getItem('shoot_refresh_token'),
      },
    });
    if (!response.success) {
      return { success: false, error: response.data?.error };
    }

    updateTokens(response.data);
    return { success: true };
  }, [updateTokens]);

  const stopImpersonation = useCallback(async () => {
    const response = await sendRequest({
      method: 'post',
      url: API.AUTH.stopImpersonation,
      headers: {
        'X-Refresh-Token': localStorage.getItem('shoot_refresh_token'),
      },
    });
    if (!response.success) {
      return { success: false, error: response.data?.error };
    }

    updateTokens(response.data);
    return { success: true };
  }, [updateTokens]);

  const signOut = useCallback(({ redirectToLogin = true } = {}) => {
    try {
      sendRequest({
        method: 'post',
        url: API.AUTH.logout,
        headers: { Authorization: `Bearer ${tokens.refreshToken}` },
      });
    } catch {
      // eslint-disable-next-line no-console
      console.log('Refresh token logout failed.');
    }
    // We must clear the timeout, otherwise we will continue trying to refresh the session

    // add token to all future requests to Shoot API
    shootAPI.defaults.headers.common.Authorization = null;
    setData({
      user: null,
      member: null,
      twoFactorToken: null,
      workspaces: null,
      activeWorkspace: null,
      refreshSessionTimeout: null,
      secretKey: null,
      qrCode: null,
    });
    setTokens({ accessToken: null, refreshToken: null });
    localStorage.removeItem('shoot_user_token');
    localStorage.removeItem('shoot_auth_token');
    localStorage.removeItem('shoot_refresh_token');
    localStorage.removeItem('shoot_twofactor_token');
    localStorage.removeItem('shoot_user');
    localStorage.removeItem('shoot_member');
    localStorage.removeItem('shoot_workspaces');
    localStorage.removeItem('shoot_active_workspace');
    localStorage.removeItem('shoot_permissions');
    localStorage.removeItem('shoot_impersonated_by');
    if (redirectToLogin) history.push('/');
  }, [history, tokens.refreshToken]);

  // A twofactor auth token was received upon signing in--redirect user to enter their code
  const startTwoFactor = ({
    twoFactorToken = null, qrCode = null, secretKey = null, redirect,
  }) => {
    if (twoFactorToken || qrCode) {
      localStorage.setItem('shoot_twofactor_token', twoFactorToken);
      shootAPI.defaults.headers.common.Authorization = `Bearer ${twoFactorToken}`;
      setData({
        ...data, twoFactorToken, qrCode, secretKey,
      });
      history.push(`/twoFactor${redirect ? `?redirect=${redirect}` : ''}`);
    }
  };

  // =========== Setters ===========
  const setWorkspaces = (workspaces) => {
    localStorage.setItem('shoot_workspaces', JSON.stringify(workspaces));
    setData({ ...data, workspaces });
  };

  const setActiveWorkspace = (activeWorkspace) => {
    localStorage.setItem('shoot_active_workspace', JSON.stringify(activeWorkspace));
    setData({ ...data, activeWorkspace });
  };

  // Handle 2FA requirement from API responses
  const handleTwoFactorRequired = useCallback((twoFactorData) => {
    // Use the proper 2FA token from the server response, fallback to temp if not provided
    let twoFactorToken = 'temp_2fa_required';

    if (!twoFactorData.twoFactorRequired && twoFactorData.twoFactorToken) {
      twoFactorToken = twoFactorData.twoFactorToken;
    }

    localStorage.setItem('shoot_twofactor_token', twoFactorToken);

    // Set authorization header for API requests
    if (twoFactorToken !== 'temp_2fa_required') {
      shootAPI.defaults.headers.common.Authorization = `Bearer ${twoFactorToken}`;
    }

    // Update state with all 2FA data at once
    setData({
      ...data,
      twoFactorToken,
      qrCode: twoFactorData.qrCode || null,
      secretKey: twoFactorData.secretKey || null,
    });
  }, [data, history]);

  // Listen for global 2FA requirement events
  useEffect(() => {
    const handleTwoFactorEvent = (event) => {
      handleTwoFactorRequired(event.detail);
    };

    window.addEventListener('twoFactorRequired', handleTwoFactorEvent);

    return () => {
      window.removeEventListener('twoFactorRequired', handleTwoFactorEvent);
    };
  }, [handleTwoFactorRequired]);

  const setUserAccount = (userToken) => {
    // updateTokens({ userToken });
    const { user } = jwtDecode(userToken);
    setData({
      ...data, user,
    });
    localStorage.setItem('shoot_user', JSON.stringify(user));
  };

  // Refresh workspace access token with updated role/permissions
  const refreshWorkspaceToken = useCallback(async () => {
    try {
      const response = await sendRequest({
        method: 'post',
        url: API.AUTH.refreshWorkspaceToken,
      });

      if (response.success && response.data?.accessToken) {
        const { accessToken } = response.data;

        // Update tokens and local storage
        updateTokens({ accessToken });

        return { success: true };
      }
      return { success: false, error: 'Failed to refresh token' };
    } catch (error) {
      console.error('Error refreshing workspace token:', error);
      return { success: false, error: error.message };
    }
  }, [updateTokens]);

  const contextValue = useMemo(() => ({
    ...data,
    ...tokens,
    redirect,
    signIn,
    signOut,
    selectWorkspace,
    startImpersonation,
    stopImpersonation,
    startTwoFactor,
    handleTwoFactorRequired,
    setActiveWorkspace,
    setWorkspaces,
    setWorkspaceMember,
    setUserAccount,
    updateTokens,
    refreshWorkspaceToken,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    data,
    tokens,
    signOut,
    handleTwoFactorRequired,
    refreshWorkspaceToken,
    startImpersonation,
    stopImpersonation,
  ]);

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
}

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
